
import React, { useState } from 'react';
import type { ApiEndpoint } from '../types';
import { api } from '../services/api';
import { 
  XMarkIcon, 
  CheckBadgeIcon, 
  CommandLineIcon, 
  ArrowPathIcon,
  MagnifyingGlassIcon,
  BoltIcon
} from './Icons';

const ENDPOINTS: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/products',
    description: 'Fetch list of products with optional geo-sorting and pagination.',
    version: '1.0.0',
    responseBody: {
      data: 'Array<Product>',
      page: 1,
      totalPages: 10,
      totalItems: 80,
      hasMore: true,
      apiVersion: '1.0.0'
    },
    authRequired: false,
    status: 'stable'
  },
  {
    method: 'POST',
    path: '/products',
    description: 'Create a new agricultural product listing.',
    version: '1.0.0',
    requestBody: {
      name: 'string',
      category: 'string',
      price: 'number',
      unit: 'string',
      description: 'string'
    },
    authRequired: true,
    status: 'stable'
  },
  {
    method: 'POST',
    path: '/auth/login',
    description: 'Authenticate a user and retrieve access tokens.',
    version: '1.0.0',
    requestBody: { contact: 'string' },
    authRequired: false,
    status: 'stable'
  },
  {
      method: 'POST',
      path: '/bids',
      description: 'Place a bid on an active auction.',
      version: '1.0.0',
      requestBody: {
          productId: 'string',
          bidderName: 'string',
          bidderContact: 'string',
          amount: 'number'
      },
      authRequired: false,
      status: 'stable'
  }
];

const ApiDocs: React.FC = () => {
  const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint | null>(null);
  const [search, setSearch] = useState('');

  const filtered = ENDPOINTS.filter(e => 
    e.path.toLowerCase().includes(search.toLowerCase()) || 
    e.description.toLowerCase().includes(search.toLowerCase())
  );

  const MethodBadge = ({ method }: { method: string }) => {
    const colors: Record<string, string> = {
      GET: 'bg-blue-100 text-blue-600',
      POST: 'bg-green-100 text-green-600',
      PUT: 'bg-yellow-100 text-yellow-600',
      PATCH: 'bg-purple-100 text-purple-600',
      DELETE: 'bg-red-100 text-red-600'
    };
    return (
      <span className={`px-2 py-0.5 rounded font-black text-[10px] ${colors[method] || 'bg-brand-green-50 text-brand-green'}`}>
        {method}
      </span>
    );
  };

  return (
    <div className="bg-card-bg rounded-3xl shadow-sm border border-card-border overflow-hidden flex flex-col md:flex-row h-[700px]">
      {/* Sidebar */}
      <div className="w-full md:w-72 border-r border-card-border flex flex-col bg-app-bg/50">
        <div className="p-4 border-b border-card-border">
          <div className="relative">
            <input 
              type="text" 
              placeholder="Search endpoints..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-4 py-2 bg-input-bg border border-input-border rounded-lg text-xs outline-none focus:ring-2 focus:ring-brand-green/20"
            />
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-app-text-secondary" />
          </div>
        </div>
        <div className="overflow-y-auto flex-grow p-2 space-y-1 custom-scrollbar">
          {filtered.map(endpoint => (
            <button 
              key={`${endpoint.method}-${endpoint.path}`}
              onClick={() => setSelectedEndpoint(endpoint)}
              className={`w-full text-left p-3 rounded-xl transition-all flex flex-col gap-1 ${
                selectedEndpoint?.path === endpoint.path && selectedEndpoint?.method === endpoint.method
                  ? 'bg-brand-green text-white shadow-md scale-[1.02]' 
                  : 'hover:bg-card-bg hover:shadow-sm text-app-text-secondary'
              }`}
            >
              <div className="flex items-center gap-2">
                <MethodBadge method={endpoint.method} />
                <span className="font-mono text-[11px] truncate">{endpoint.path}</span>
              </div>
              <p className={`text-[10px] truncate ${selectedEndpoint?.path === endpoint.path ? 'text-white/70' : 'text-app-text-secondary'}`}>
                {endpoint.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Detail View */}
      <div className="flex-grow overflow-y-auto p-6 md:p-10 custom-scrollbar">
        {selectedEndpoint ? (
          <div className="space-y-8 animate-in fade-in duration-300">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <MethodBadge method={selectedEndpoint.method} />
                  <h2 className="text-2xl font-black text-brand-green font-mono">{api.base}{selectedEndpoint.path}</h2>
                </div>
                <p className="text-app-text-secondary font-medium">{selectedEndpoint.description}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className="bg-brand-yellow/10 text-brand-brown text-[10px] font-black px-2 py-1 rounded-full uppercase">
                  Version {selectedEndpoint.version}
                </span>
                {selectedEndpoint.authRequired && (
                  <span className="bg-red-100 text-red-600 text-[10px] font-black px-2 py-1 rounded-full uppercase flex items-center gap-1">
                    <BoltIcon className="w-3 h-3" />
                    Auth Required
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {selectedEndpoint.requestBody && (
                <section>
                  <h3 className="text-xs font-black uppercase text-app-text-secondary tracking-widest mb-3 flex items-center gap-2">
                    <CommandLineIcon className="w-4 h-4" />
                    Request Body (JSON)
                  </h3>
                  <div className="bg-[#1e1e1e] p-4 rounded-2xl overflow-x-auto border border-white/5">
                    <pre className="text-green-400 text-xs font-mono">
                      {JSON.stringify(selectedEndpoint.requestBody, null, 2)}
                    </pre>
                  </div>
                </section>
              )}

              <section>
                <h3 className="text-xs font-black uppercase text-app-text-secondary tracking-widest mb-3 flex items-center gap-2">
                  <CheckBadgeIcon className="w-4 h-4" />
                  Response Pattern
                </h3>
                <div className="bg-[#1e1e1e] p-4 rounded-2xl overflow-x-auto border border-white/5">
                  <pre className="text-blue-400 text-xs font-mono">
                    {JSON.stringify(selectedEndpoint.responseBody || { success: true }, null, 2)}
                  </pre>
                </div>
              </section>

              <section className="bg-brand-cream/30 p-6 rounded-2xl border border-brand-cream border-dashed">
                  <h4 className="text-[10px] font-black uppercase text-app-text-secondary mb-2">Simulation Sandbox</h4>
                  <button 
                    disabled
                    className="bg-btn-disabled-bg text-app-text px-6 py-2 rounded-xl text-xs font-black uppercase cursor-not-allowed flex items-center gap-2"
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                    Try it out (Disabled in Demo)
                  </button>
                  <p className="text-[10px] text-app-text-secondary mt-3 italic font-medium">
                    "Live testing is available via Postman collection exports in Phase 15 production environments."
                  </p>
              </section>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center text-app-text-secondary">
            <div className="text-6xl mb-4">📖</div>
            <h2 className="text-2xl font-black text-brand-brown">AgriConnect API Explorer</h2>
            <p className="max-w-xs font-bold mt-2">Select an endpoint from the sidebar to view detailed documentation and schemas.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiDocs;
